import { useReducer, useEffect, useMemo } from "react";
import type { z } from "zod";
import { authStrategy } from "../auth";
import type { Token } from "../auth/types";

type LoginTokenResult<T extends z.ZodType | undefined = undefined> =
  T extends z.ZodType ? z.infer<T> | null : Token | null;

/**
 * @returns null if not yet logged in. Object with token and parsedToken if
 * logged in.
 */
export const useLoginToken = <T extends z.ZodType | undefined = undefined>(
  tokenSchema?: T
): LoginTokenResult<T> => {
  const [token, dispatch] = useReducer(
    authStrategy.getToken,
    undefined,
    authStrategy.getToken
  );

  const memoToken = useMemo(() => {
    if (!token) return null;
    if (!tokenSchema) return token;

    const result = tokenSchema.safeParse(token);
    if (!result.success) {
      console.error("Token validation failed:", result.error);
      return null;
    }

    return result.data;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token?.token, tokenSchema]);

  useEffect(() => {
    const unsubscribe = authStrategy.onTokenUpdate(dispatch);
    authStrategy.getTokenP().then(dispatch).catch(console.warn);
    return unsubscribe;
  }, []);

  return memoToken;
};
